import React, { useState } from "react";
import { Typography, Input, Button, Link, CircularProgress, Box as MuiBox } from "@mui/material";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/system";
import config from "../config";
import {
  PageContainer,
  ContentContainer,
} from "../components/ContentContainer";

const ImageRaw = styled("div")({
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  margin: "10px",
});

const CenterContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ImageCard = styled("img")({
  width: "150px",
  height: "auto",
  marginRight: "20px",
  marginLeft: "20px",
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
});

const ImageWithLabel = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const InfoLabel = styled("div")({
  fontSize: "16px",
  color: "#555",
});

const SearchPage = () => {
  const navigate = useNavigate();
  const [productId, setProductId] = useState("");
  const [productPhoto, setProductPhoto] = useState(null);
  const [reconstructedIds, setReconstructedIds] = useState([]);
  const [notionIds, setNotionIds] = useState([]);
  const [reconstructedPhotos, setReconstructedPhotos] = useState([]);
  const [notionPhotos, setNotionPhotos] = useState([]);
  const [loadingIds, setLoadingIds] = useState(false);
  const [loadingNotion, setLoadingNotion] = useState(false);
  const [loadingReconstructed, setLoadingReconstructed] = useState(false);

  const handleInputChange = (event) => {
    setProductId(event.target.value);
  };

  const handleProcessClick = async () => {
    try {
      // reset state and show loading placeholders
      setLoadingIds(true);
      setLoadingNotion(false);
      setLoadingReconstructed(false);
      setProductPhoto(null);
      setReconstructedIds([]);
      setNotionIds([]);
      setReconstructedPhotos([]);
      setNotionPhotos([]);

      const productResponse = await axios.get(
        `${config.productUrl}${productId}`
      );
      if (!productResponse.data) {
        setProductId("");
        setLoadingIds(false);
        return;
      }
      const productImg = productResponse.data.cover_url;
      setProductPhoto(productImg);

      const reconstructedIdsResponse = await axios.get(
        `${config.searchUrl}${productId}`
      );

      const reconstructedIds = reconstructedIdsResponse.data.reconstructed_ids;
      setReconstructedIds(reconstructedIds);
      const notionIds = reconstructedIdsResponse.data.notion_ids;
      setNotionIds(notionIds);
      // initialize placeholders for images
      setReconstructedPhotos(Array.isArray(reconstructedIds) ? new Array(reconstructedIds.length).fill(null) : []);
      setNotionPhotos(Array.isArray(notionIds) ? new Array(notionIds.length).fill(null) : []);
      setLoadingIds(false);
      setLoadingNotion(true);
      setLoadingReconstructed(true);

      const notionImagePromises = notionIds.map((id) =>
        axios.get(`${config.productUrl}${id}`)
      );
      const notionProductsResponses = await Promise.all(notionImagePromises);
      const notionImages = notionProductsResponses.map(
        (response) => response.data.cover_url
      );
      setNotionPhotos(notionImages);
      setLoadingNotion(false);

      const reconstructedImagePromises = reconstructedIds.map((id) =>
        axios.get(`${config.productUrl}${id}`)
      );
      const reconstructedProductsResponses = await Promise.all(
        reconstructedImagePromises
      );
      const reconstructedImages = reconstructedProductsResponses.map(
        (response) => response.data.cover_url
      );
      setReconstructedPhotos(reconstructedImages);
      setLoadingReconstructed(false);
    } catch (error) {
      console.log(error);
      setLoadingIds(false);
      setLoadingNotion(false);
      setLoadingReconstructed(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar navigate={navigate} />
      <ContentContainer>
        <CenterContainer>
          <Typography variant="h3" sx={{ margin: "10px" }}>
            Search Tool
          </Typography>
          <Input
            type="text"
            value={productId}
            onChange={handleInputChange}
            placeholder="Enter product ID"
            sx={{ margin: "10px" }}
          />
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleProcessClick}
            sx={{ margin: "10px" }}
          >
            Process
          </Button>
          {productPhoto && (
            <div>
              <Typography variant="h4" sx={{ margin: "5px" }}>
                Product Photo:
              </Typography>
              <ImageCard src={productPhoto} />
            </div>
          )}
          {(loadingReconstructed || reconstructedPhotos.length > 0) && (
            <Typography variant="h4" sx={{ margin: "5px" }}>
              Similar reconstructed products
            </Typography>
          )}
          <ImageRaw>
            {(loadingReconstructed || reconstructedPhotos.length > 0) &&
              (reconstructedPhotos.length ? reconstructedPhotos : []).map((image, index) => (
                <ImageWithLabel key={"reconstructed_" + index}>
                  {image ? (
                    <>
                      <ImageCard key={"reconstructed_card_" + index} src={image} />
                      <InfoLabel key={"reconstructed_label_" + index}>
                        Product id:
                        <Link
                          key={index}
                          href={`https://portalvhdskzpngkvpz6x48.blob.core.windows.net/3d-models/${productId}/${productId}_65.blend`}
                          passHref
                        >
                          <a target="_blank" rel="noopener noreferrer">
                            {reconstructedIds[index]}
                          </a>
                        </Link>
                      </InfoLabel>
                    </>
                  ) : (
                    <MuiBox sx={{ width: 150, height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: '1px dashed rgba(0,0,0,0.2)', margin: '0 20px' }}>
                      <CircularProgress size={28} />
                    </MuiBox>
                  )}
                </ImageWithLabel>
              ))}
          </ImageRaw>
          {(loadingNotion || notionPhotos.length > 0) && (
            <Typography variant="h4" sx={{ margin: "5px" }}>
              Similar products on Notion board
            </Typography>
          )}
          <ImageRaw>
            {(loadingNotion || notionPhotos.length > 0) &&
              (notionPhotos.length ? notionPhotos : []).map((image, index) => (
                <ImageWithLabel key={"notion_" + index}>
                  {image ? (
                    <>
                      <ImageCard key={"notion_card_" + index} src={image} />
                      <InfoLabel key={"reconstructed_label_" + index}>
                        Product id: {notionIds[index]}
                      </InfoLabel>
                    </>
                  ) : (
                    <MuiBox sx={{ width: 150, height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: '1px dashed rgba(0,0,0,0.2)', margin: '0 20px' }}>
                      <CircularProgress size={28} />
                    </MuiBox>
                  )}
                </ImageWithLabel>
              ))}
          </ImageRaw>
        </CenterContainer>
      </ContentContainer>
    </PageContainer>
  );
};

export default SearchPage;
